interface RetryOptions {
  maxRetries?: number;
  baseDelay?: number;
  shouldRetry?: (error: Error, attempt: number) => boolean | Promise<boolean>;
}

interface FallbackConfig {
  emptyResponse?: string;
  errorResponse?: string;
}

interface RequestConfig<T> extends Omit<RequestInit, 'body'> {
  retry?: RetryOptions;
  body?: T;
  isStream?: boolean;
  fallback?: FallbackConfig;
}

interface APIError extends Error {
  status?: number;
  data?: unknown;
}

const defaultRetryOptions: Required<RetryOptions> = {
  maxRetries: 3,
  baseDelay: 1000,
  shouldRetry: (error: Error) => {
    // By default, retry on network errors and 5xx server errors
    if (error instanceof TypeError) {
      return true; // Network errors
    }

    const apiError = error as APIError;

    return apiError.status ? apiError.status >= 500 : false;
  },
};

const defaultFallbackConfig: Required<FallbackConfig> = {
  emptyResponse: "I apologize, but I couldn't generate an answer at this time. Please try again.",
  errorResponse: 'I encountered an error while processing your request. Please try again.',
};

let globalRetryOptions: Required<RetryOptions> = { ...defaultRetryOptions };
let globalFallbackConfig: Required<FallbackConfig> = { ...defaultFallbackConfig };

export function configureRetry(options: RetryOptions): void {
  globalRetryOptions = {
    ...defaultRetryOptions,
    ...options,
  };
}

export function configureFallback(config: FallbackConfig): void {
  globalFallbackConfig = {
    ...defaultFallbackConfig,
    ...config,
  };
}

async function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export async function makeRequest<TResponse, TBody = unknown>(url: string | URL, config: RequestConfig<TBody> = {}): Promise<TResponse> {
  const { retry = {}, headers = {}, body, isStream = false, fallback = {}, ...fetchConfig } = config;
  const retryOptions = {
    ...globalRetryOptions,
    ...retry,
  };
  const fallbackOptions = {
    ...globalFallbackConfig,
    ...fallback,
  };

  let attempt = 1;

  while (true) {
    try {
      const response = await fetch(url, {
        ...fetchConfig,
        headers: {
          'Content-Type': 'application/json',
          ...headers,
        },
        body: body ? JSON.stringify(body) : undefined,
      });

      if (response.ok) {
        // For streaming responses, return the response object directly
        if (isStream) {
          if (!response.ok) {
            throw new Error('Failed to fetch answer');
          }

          return response as unknown as TResponse;
        }

        const data = await response.json();
        // Check for empty response in JSON
        if (!data || (typeof data === 'object' && Object.keys(data).length === 0)) {
          const emptyError = new Error(fallbackOptions.emptyResponse);
          emptyError.name = 'EmptyResponseError';
          throw emptyError;
        }

        return data as TResponse;
      }

      const error = new Error(fallbackOptions.errorResponse) as APIError;
      error.status = response.status;
      error.data = await response.json().catch(() => undefined);
      throw error;
    } catch (error) {
      const shouldAttemptRetry = await Promise.resolve(retryOptions.shouldRetry(error as Error, attempt));

      if (attempt >= retryOptions.maxRetries || !shouldAttemptRetry) {
        if (error instanceof Error && error.name === 'EmptyResponseError') {
          return { message: error.message } as TResponse;
        }
        throw error;
      }

      console.warn(`Request failed, attempt ${attempt} of ${retryOptions.maxRetries}. Retrying...`);

      // Exponential backoff with jitter to prevent thundering herd
      const jitter = Math.random() * 0.3 + 0.85; // Random between 0.85-1.15
      await delay(retryOptions.baseDelay * Math.pow(2, attempt - 1) * jitter);
      attempt++;
    }
  }
}
